import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@betty/i18n';
import { Box } from '@betty/moonlight';
import { CommunityJackpotWinnersProps } from './CommunityJackpotWinners.types';
import useMilestoneWinners from '../../hooks/useMilestoneWinners/useMilestoneWinners';
import {
  getFlipReward,
  getJackpotWinner,
  getMilestoneMaker,
  getOtherPrizes,
} from './CommunityJackpotWinners.utils';
import { useSelector } from '@betty/toolkit/state';
import { selectCommunityJackpotMilestone } from '../../state';
import CommunityJackpotWinnerCard from './CommunityJackpotWinnerCard/CommunityJackpotWinnerCard';
import CommunityJackpotTable from '../CommunityJackpot/CommunityJackpotTable/CommunityJackpotTable';
import CommunityJackpotWinnersSkeleton from './CommunityJackpotWinnersSkeleton';

function CommunityJackpotWinners({
  milestoneId,
}: CommunityJackpotWinnersProps) {
  const { t } = useTranslation('communityJackpot');
  const { data, dataState } = useMilestoneWinners(milestoneId);
  const milestone = useSelector((state) =>
    selectCommunityJackpotMilestone(state, milestoneId),
  );
  const flipReward = useMemo(() => getFlipReward(milestone), [milestone]);
  const [jackpotWinner, milestoneMaker, otherPrizes] = useMemo(
    () => [
      getJackpotWinner(data),
      getMilestoneMaker(data),
      getOtherPrizes(data),
    ],
    [data],
  );

  const renderHeader = useCallback(
    () => (
      <Box gap="space-800" paddingBottom="space-400">
        <CommunityJackpotWinnerCard
          type="Prize"
          label={t('milestoneWinnerCardLabel', {
            label: milestone?.label || t('milestoneWinnerCardJackpot'),
          })}
          amount={milestone?.rewardAmount}
          name={jackpotWinner?.screenName}
          status={jackpotWinner?.status}
        />
        {milestoneMaker && (
          <CommunityJackpotWinnerCard
            type="Bonus"
            label={t('milestoneWinnerCardMilestoneMaker')}
            info={t('milestoneMakerBonusInfo')}
            amount={flipReward?.rewardAmount}
            name={milestoneMaker?.screenName}
            status={milestoneMaker?.status}
          />
        )}
      </Box>
    ),
    [
      flipReward?.rewardAmount,
      jackpotWinner?.screenName,
      jackpotWinner?.status,
      milestone?.label,
      milestone?.rewardAmount,
      milestoneMaker,
      t,
    ],
  );

  if (dataState !== 'fulfilled') {
    return <CommunityJackpotWinnersSkeleton />;
  }

  return (
    <CommunityJackpotTable
      variant="winners"
      prizes={otherPrizes}
      renderHeader={renderHeader}
    />
  );
}

export default memo(CommunityJackpotWinners);
